import React, { useRef, useEffect } from "react";
import { HiCheckCircle } from "react-icons/hi";
import { Button } from "@components";
import { modalSurface, textHeading, Stack } from "@design-system";

interface SuccessPopupAction {
  text: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "success" | "danger";
  icon?: React.ReactNode;
}

interface SuccessPopupProps {
  isOpen: boolean;
  title: string;
  message: string;
  primaryAction: SuccessPopupAction;
  secondaryAction?: SuccessPopupAction;
  onClose: () => void;
}

export const SuccessPopup: React.FC<SuccessPopupProps> = ({
  isOpen,
  title,
  message,
  primaryAction,
  secondaryAction,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal
  const handleOutsideClick = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      onClose();
    }
  };

  // Add and remove event listener for clicks
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]">
      <div
        ref={modalRef}
        className={`${modalSurface} p-8 max-w-md w-full transform transition-all animate-fadeIn`}
      >
        <div className="flex flex-col items-center">
          <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full mb-6">
            <HiCheckCircle className="text-green-500 dark:text-green-400 h-16 w-16" />
          </div>
          <h2 className={`text-2xl font-bold mb-3 text-center ${textHeading}`}>
            {title}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 text-center">
            {message}
          </p>
          <Stack gap={4} className="w-full">
            <Button
              variant={primaryAction.variant || "success"}
              onClick={primaryAction.onClick}
              icon={primaryAction.icon}
              className="w-full py-3 sm:py-2.5"
            >
              {primaryAction.text}
            </Button>

            {secondaryAction && (
              <Button
                variant={secondaryAction.variant || "secondary"}
                onClick={secondaryAction.onClick}
                icon={secondaryAction.icon}
                className="w-full py-3 sm:py-2.5"
              >
                {secondaryAction.text}
              </Button>
            )}
          </Stack>
        </div>
      </div>
    </div>
  );
};
